/*
  ----------------------------------------------------------------------------------------------------
  MAISON — CART BUNDLE CONTROLS
  ----------------------------------------------------------------------------------------------------

  The cart draws a bundle as one block: the bundle's own name and picture on top, its products
  stacked underneath. Its quantity and remove controls therefore have to act on every line of the
  group at once.

  The theme cannot do that on its own. Its <line-item-quantity> reads one `id` and one `quantity`
  out of the link's href and calls the cart change route, which only ever touches a single line --
  so "remove" would take the lens out of the cart and leave the flash sitting there alone, at full
  price, because the automatic discount no longer applies to it.

  This listener catches the click first and sends one /cart/update.js for the whole group instead.
  The links keep their real hrefs, so without JS they still work, one line at a time.

  Bound in the capture phase on `document`: <line-item-quantity> listens on itself, which is deeper
  in the tree, so a bubbling listener here would run second and the theme would have fired its own
  request already.
*/
(() => {
  const SELECTOR = 'a[data-bundle-remove], a[data-bundle-quantity]';

  // One request at a time. Two clicks on "+" would otherwise both read the quantity that was on the
  // page when they were rendered, and the second would undo the first.
  let busy = false;

  document.addEventListener('click', onClick, true);

  function onClick(event) {
    const link = event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!link) return;

    const block = link.closest('line-item[data-bundle-members]');
    if (!block) return;

    let members;

    try {
      members = JSON.parse(block.dataset.bundleMembers);
    } catch (error) {
      // Unreadable data is the one case where the theme's own single-line behaviour is better than
      // nothing: leave the click alone and let the link do what it says.
      return;
    }

    if (!Array.isArray(members) || members.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    if (busy) return;

    /*
      A removal is quantity 0 for every line. Otherwise each line follows the bundle's new quantity
      multiplied by how many of that product one bundle holds, so raising the bundle to 2 doubles
      every part instead of flattening them to the same number.
    */
    const isRemoval = link.hasAttribute('data-bundle-remove');
    const quantity = isRemoval ? 0 : parseInt(link.dataset.bundleQuantity, 10);

    if (!isRemoval && (isNaN(quantity) || quantity < 0)) return;

    const updates = {};

    members.forEach((member) => {
      if (!member || !member.key) return;
      const ratio = parseInt(member.ratio, 10) || 1;
      updates[member.key] = quantity === 0 ? 0 : quantity * ratio;
    });

    if (Object.keys(updates).length === 0) return;

    update(block, updates, quantity);
  }

  async function update(block, updates, quantity) {
    busy = true;

    // The theme's own pill loader, shown the way <line-item-quantity> shows it.
    block.dispatchEvent(
      new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity: quantity } })
    );

    const base = block.dataset.cartUpdateUrl || '/cart/update';

    try {
      const response = await fetch(`${base}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw await response.json();

      const cart = await response.json();

      /*
        BOGOS keeps its own copy of the cart and does not watch plain fetches, so its free gifts
        would still be attached to a bundle that is no longer there. This is the event its bundle
        subscribes to; the handler clears that cache.
      */
      document.dispatchEvent(new CustomEvent('bogos:cart-change', { bubbles: true }));

      // The drawer re-renders from these, which is also what removes the block from the DOM.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'maison-cart-bundle', cart } })
      );

      block.dispatchEvent(
        new CustomEvent('line-item:change', { bubbles: true, detail: { cart, quantity } })
      );
    } catch (error) {
      block.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));

      const target = block.querySelector('[role="alert"]');
      if (target) target.textContent = (error && error.description) || (error && error.message) || '';
    } finally {
      busy = false;
    }
  }
})();
